import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';
import { useHistory, useParams } from 'react-router-dom';

import BetaCategoryPage from 'components/upb_viewer_beta/BetaCategoryPage';
import BetaFunnelCard from 'components/upb_viewer_beta/BetaFunnelCard';
import BetaKpiStrip from 'components/upb_viewer_beta/BetaKpiStrip';
import BetaL1Sidebar from 'components/upb_viewer_beta/BetaL1Sidebar';
import BetaProductsPage from 'components/upb_viewer_beta/BetaProductsPage';
import BetaTopBar from 'components/upb_viewer_beta/BetaTopBar';
import useUPBBetaChildCategoryStats from 'components/upb_viewer_beta/hooks/useUPBBetaChildCategoryStats';
import useUPBBetaKpiMetrics from 'components/upb_viewer_beta/hooks/useUPBBetaKpiMetrics';
import useGet from 'lib/hooks/useGet';
import { getCategoryIDFromUrl } from 'components/upb_viewer/UPBViewer';
import {
  TAXONOMY_CATEGORY_LEVEL_OPTIONS_WITH_SUB_OPTIONS,
  UPB_VIEWER_BETA_CATEGORY_GMV,
} from 'lib/networking/endpoints';
import {
  BG_2,
  FONT_BODY,
  GLOBAL_NAV_HEIGHT,
  KPI_STRIP_HEIGHT,
  SIDEBAR_CARD_WIDTH,
  TOPBAR_HEIGHT,
} from 'components/upb_viewer_beta/css/betaTokens';

const BASE_URL = 'upb-viewer-beta';

/** Format a SKU / L4 count for display (e.g. 1992970 → "1,992,970") */
const formatCount = n => (n != null ? Number(n).toLocaleString('en-US') : '—');

// KPI metric key constants
const KPI_KEYS = {
  ANNUALIZED_GMV: 'annualized_gmv',
  TOTAL_SKUS: 'total_skus',
  ACTIVE_SKUS: 'active_skus',
  GMV_SKUS: 'gmv_skus',
  AVG_ECOM_SCORE: 'avg_ecom_score',
  AVG_ATTRIBUTE_SCORE: 'avg_attribute_score',
  TOTAL_L4S: 'total_l4s',
};

/**
 * Build the 7-metric array for BetaKpiStrip from the API response.
 */
function buildMetrics(data) {
  if (!data) {
    return [
      { key: KPI_KEYS.ANNUALIZED_GMV, label: 'Annualized GMV', value: null },
      { key: KPI_KEYS.TOTAL_SKUS, label: 'Total SKUs', value: null },
      { key: KPI_KEYS.ACTIVE_SKUS, label: 'Active SKUs', value: null },
      { key: KPI_KEYS.GMV_SKUS, label: 'GMV SKUs', value: null },
      { key: KPI_KEYS.AVG_ECOM_SCORE, label: 'Avg eCom Score', value: null },
      { key: KPI_KEYS.AVG_ATTRIBUTE_SCORE, label: 'Avg Attribute Score', value: null },
      { key: KPI_KEYS.TOTAL_L4S, label: 'Total L4s', value: null },
    ];
  }

  const fmt = (n, formatter) => (n != null ? formatter(n) : '—');

  return [
    {
      key: KPI_KEYS.ANNUALIZED_GMV,
      label: 'Annualized GMV',
      // Full dollar amount with commas (e.g. $101,234,567), not abbreviated;
      // em-dash when there's no GMV.
      value: data.annualized_gmv
        ? `$${Math.round(data.annualized_gmv).toLocaleString('en-US')}`
        : '—',
      tooltip: 'Total annualized GMV (L30d).',
    },
    {
      key: KPI_KEYS.TOTAL_SKUS,
      label: 'Total SKUs',
      value: fmt(data.total_skus, formatCount),
      tooltip: 'All SKUs shared by a DP',
    },
    {
      key: KPI_KEYS.ACTIVE_SKUS,
      label: 'Active SKUs',
      value: fmt(data.active_skus, formatCount),
      tooltip: 'Total SKUs marked as active by a DP',
    },
    {
      key: KPI_KEYS.GMV_SKUS,
      label: 'GMV SKUs',
      value: fmt(data.gmv_skus, formatCount),
      tooltip: 'Total SKUs with GMV in L30D',
    },
    {
      key: KPI_KEYS.AVG_ECOM_SCORE,
      label: 'Avg eCom Score',
      value: fmt(data.avg_ecom_score, v => `${Math.round(v)}%`),
      tooltip: 'Average eCom score for active products',
    },
    {
      key: KPI_KEYS.AVG_ATTRIBUTE_SCORE,
      label: 'Avg Attribute Score',
      value: fmt(data.avg_attribute_score, v => `${Math.round(v)}%`),
      tooltip: 'Average attribute attribute for active SKUs',
    },
    {
      key: KPI_KEYS.TOTAL_L4S,
      label: 'Total L4s',
      value: fmt(data.total_l4s, formatCount),
      tooltip: 'Number of L4 sub-categories',
    },
  ];
}

// SKU type filter — display only; the backend funnel/KPI endpoints do not yet
// support a sku_type param, so this drives the label but not the query.
const SKU_TYPES = [
  { value: 'total', label: 'Total SKUs' },
  { value: 'active', label: 'Active SKUs' },
  { value: 'gmv', label: 'GMV SKUs' },
];

function UPBViewerBetaContainer({ navExpanded }) {
  const history = useHistory();
  const { l1_category, l2_category, l3_category, l4_category } = useParams();

  const [activeMetric, setActiveMetric] = useState(KPI_KEYS.ANNUALIZED_GMV);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedL1Id, setSelectedL1Id] = useState(null);
  const [vendorId, setVendorId] = useState(null);
  // Distributor-group filter (Influence campaign `third_party` tag). Mutually
  // exclusive with a single vendorId — the dropdown reports both at once.
  const [thirdPartyTag, setThirdPartyTag] = useState(null);
  const [skuType, setSkuType] = useState('active');

  const handleDistributorChange = ({ vendorId: nextVendorId, thirdPartyTag: nextTag }) => {
    setVendorId(nextVendorId);
    setThirdPartyTag(nextTag);
  };

  // URL → ids
  const urlL1Id = getCategoryIDFromUrl(l1_category);
  const urlL2Id = getCategoryIDFromUrl(l2_category);
  const urlL3Id = getCategoryIDFromUrl(l3_category);
  const urlL4Id = getCategoryIDFromUrl(l4_category);

  const trimmedSearch = (searchQuery || '').trim();
  const hasSearch = trimmedSearch.length > 0;
  const hasCategoryParam = Boolean(l1_category);
  const onRoot = !hasCategoryParam && !hasSearch;

  // URL wins for the effective L1 when present; state drives the root funnel.
  const effectiveL1Id = urlL1Id != null ? Number(urlL1Id) : selectedL1Id;

  // Expand state — single source of truth. Seeded from the URL so the L1 we're
  // inside is open by default, but the chevron can collapse it freely. When the
  // URL's L1 changes (navigation to a different L1), auto-expand the new one.
  const [expandedL1Id, setExpandedL1Id] = useState(urlL1Id != null ? Number(urlL1Id) : null);
  useEffect(() => {
    if (urlL1Id != null) setExpandedL1Id(Number(urlL1Id));
  }, [urlL1Id]);
  const effectiveExpandedL1Id = expandedL1Id;

  const handleToggleExpand = id => {
    setExpandedL1Id(prev => (prev === id ? null : id));
  };

  // Navigating the category tree (sidebar / breadcrumb / drilldown) exits search:
  // clear the search term so we return to the normal category view. Clearing
  // searchQuery also clears the search box (the top bar's draft syncs from it).
  const handleNavigate = url => {
    setSearchQuery('');
    history.push(url);
  };

  // L1 categories — shared by the sidebar and the pane-head Category dropdown
  const { data: taxonomyData, loading: categoriesLoading } = useGet(
    TAXONOMY_CATEGORY_LEVEL_OPTIONS_WITH_SUB_OPTIONS,
    { is_upb: true },
  );
  // Memoised so the `|| []` fallback doesn't hand out a fresh array on every
  // render, which would defeat the GMV sort memo below.
  const categories = useMemo(() => taxonomyData?.data || [], [taxonomyData]);

  // Per-L1 annualized GMV — keyed by category id; total feeds "All Categories".
  // Vendor-aware so the sidebar matches the vendor-filtered KPI total. Only send
  // vendor_id when a distributor is selected — passing `undefined` serializes to
  // the string "undefined", which the Integer query schema rejects (422) and
  // leaves the sidebar GMV blank.
  const l1GmvParams = useMemo(() => {
    if (vendorId) return { vendor_id: vendorId };
    if (thirdPartyTag) return { third_party_tag: thirdPartyTag };
    return {};
  }, [vendorId, thirdPartyTag]);
  const { data: l1GmvData } = useGet(UPB_VIEWER_BETA_CATEGORY_GMV, l1GmvParams);
  const { gmvByL1Id, totalGmv } = useMemo(() => {
    const rows = l1GmvData?.data || [];
    const map = {};
    let total = 0;
    rows.forEach(row => {
      const gmv = Number(row.ANNUALIZED_GMV) || 0;
      map[Number(row.CAT_VALUE)] = gmv;
      total += gmv;
    });
    return { gmvByL1Id: map, totalGmv: total };
  }, [l1GmvData]);

  // Sidebar L1 order: annualized GMV, descending — the taxonomy endpoint returns
  // an arbitrary order. Categories with no GMV row sort last, and ties keep the
  // endpoint's original order (index tiebreak). Copy before sorting: `categories`
  // is the useGet hook's cached data and must not be mutated in place.
  const categoriesByGmvDesc = useMemo(
    () =>
      categories
        .map((cat, index) => ({ cat, index }))
        .sort((a, b) => {
          const aGmv = Number.isFinite(gmvByL1Id[a.cat.id]) ? gmvByL1Id[a.cat.id] : -Infinity;
          const bGmv = Number.isFinite(gmvByL1Id[b.cat.id]) ? gmvByL1Id[b.cat.id] : -Infinity;
          return bGmv - aGmv || a.index - b.index;
        })
        .map(({ cat }) => cat),
    [categories, gmvByL1Id],
  );

  // Per-L2 GMV for the expanded L1 in the sidebar. Reuses the child-category-stats
  // endpoint that already powers the category page; the request is only issued
  // when a row is expanded, and the response is cached by L1 (+ vendor).
  const { data: expandedL1ChildStats } = useUPBBetaChildCategoryStats({
    l1CategoryId: effectiveExpandedL1Id || undefined,
    vendorId: vendorId || undefined,
    thirdPartyTag: thirdPartyTag || undefined,
    skip: !effectiveExpandedL1Id,
  });
  const gmvByL2Id = useMemo(() => {
    const rows = expandedL1ChildStats?.data || [];
    const map = {};
    rows.forEach(row => {
      if (row && row.child_id != null) {
        map[Number(row.child_id)] = Number(row.annualized_gmv) || 0;
      }
    });
    return map;
  }, [expandedL1ChildStats]);

  // KPI strip — reflects the deepest category in the URL (or selected L1 on root)
  const { data: kpiData, loading: kpiLoading } = useUPBBetaKpiMetrics({
    l1CategoryId: effectiveL1Id || undefined,
    l2CategoryId: urlL2Id || undefined,
    l3CategoryId: urlL3Id || undefined,
    l4CategoryId: urlL4Id || undefined,
    vendorId: vendorId || undefined,
    thirdPartyTag: thirdPartyTag || undefined,
  });

  const metrics = buildMetrics(kpiData);

  const urlParams = {
    l1: l1_category,
    l2: l2_category,
    l3: l3_category,
    l4: l4_category,
  };

  const renderBody = () => {
    if (onRoot) {
      // Funnel view — unchanged from PR 2.
      return (
        <>
          {/* No onSelect: an L1 click navigates into the category page (the L2 card
              grid), consistent with clicking L1 from a deeper level. The funnel's
              own Category dropdown still filters the funnel. */}
          <BetaL1Sidebar
            categories={categoriesByGmvDesc}
            loading={categoriesLoading}
            selectedL1Id={effectiveL1Id}
            searchActive={hasSearch}
            expandedL1Id={effectiveExpandedL1Id}
            onToggleExpand={handleToggleExpand}
            onNavigate={handleNavigate}
            baseURL={BASE_URL}
            gmvByL1Id={gmvByL1Id}
            gmvByL2Id={gmvByL2Id}
            totalGmv={totalGmv}
          />
          <BetaFunnelCard
            l1CategoryId={effectiveL1Id}
            onL1Change={setSelectedL1Id}
            vendorId={vendorId}
            thirdPartyTag={thirdPartyTag}
            categories={categories}
            skuType={skuType}
            skuTypeOptions={SKU_TYPES}
            onSkuTypeChange={setSkuType}
          />
        </>
      );
    }

    const showProducts = hasSearch || Boolean(l4_category);

    return (
      <>
        <BetaL1Sidebar
          categories={categoriesByGmvDesc}
          loading={categoriesLoading}
          selectedL1Id={effectiveL1Id}
          selectedL2Id={urlL2Id != null ? Number(urlL2Id) : null}
          searchActive={hasSearch}
          expandedL1Id={effectiveExpandedL1Id}
          onToggleExpand={handleToggleExpand}
          onNavigate={handleNavigate}
          baseURL={BASE_URL}
          gmvByL1Id={gmvByL1Id}
          gmvByL2Id={gmvByL2Id}
          totalGmv={totalGmv}
        />
        {showProducts ? (
          <BetaProductsPage
            urlParams={urlParams}
            searchQuery={trimmedSearch}
            baseURL={BASE_URL}
            skuType={skuType}
            skuTypeOptions={SKU_TYPES}
            onSkuTypeChange={setSkuType}
            // Total $ GMV for the L4 we're inside — used to compute per-product
            // market share client-side (Snowflake's PERCENTAGE column on the
            // L5 row is a tiny global share, not per-L4, so we replace it).
            categoryTotalGmv={kpiData?.annualized_gmv || 0}
            onNavigate={handleNavigate}
          />
        ) : (
          <BetaCategoryPage
            urlParams={urlParams}
            onNavigate={handleNavigate}
            baseURL={BASE_URL}
            vendorId={vendorId}
            thirdPartyTag={thirdPartyTag}
            skuType={skuType}
            skuTypeOptions={SKU_TYPES}
            onSkuTypeChange={setSkuType}
          />
        )}
      </>
    );
  };

  return (
    <div className={css(styles.pageWrapper)}>
      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <BetaTopBar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        navExpanded={navExpanded}
      />

      {/* ── KPI strip ───────────────────────────────────────────────────── */}
      <BetaKpiStrip
        metrics={metrics}
        activeMetric={activeMetric}
        onMetricChange={setActiveMetric}
        loading={kpiLoading}
        vendorId={vendorId}
        thirdPartyTag={thirdPartyTag}
        onVendorChange={handleDistributorChange}
        navExpanded={navExpanded}
      />

      {/* ── Content grid: sidebar + body ─────────────────────────────────── */}
      <div className={css(styles.contentGrid)}>{renderBody()}</div>
    </div>
  );
}

// The beta's own fixed chrome (top bar + KPI strip). The page sits below the
// global navbar in normal flow, so marginTop only needs to clear this chrome,
// while the scroll-area height must also subtract the global navbar.
const FIXED_CHROME = TOPBAR_HEIGHT + KPI_STRIP_HEIGHT;

const styles = StyleSheet.create({
  pageWrapper: {
    backgroundColor: BG_2,
    fontFamily: FONT_BODY,
    minHeight: `calc(100vh - ${GLOBAL_NAV_HEIGHT}px)`,
  },

  contentGrid: {
    display: 'grid',
    gridTemplateColumns: `${SIDEBAR_CARD_WIDTH}px 1fr`,
    gap: 20,
    padding: '20px 24px 40px',
    marginTop: FIXED_CHROME,
    overflowY: 'auto',
    height: `calc(100vh - ${GLOBAL_NAV_HEIGHT + FIXED_CHROME}px)`,
  },
});

UPBViewerBetaContainer.propTypes = {
  // True when the global side-nav drawer is open; shifts the fixed chrome right
  // to stay aligned with the body content (which FullScreenMainContainer pushes).
  navExpanded: PropTypes.bool,
};

export default UPBViewerBetaContainer;
